import { app } from '../../app.module'
// アカウントデータ
export interface IAccountData {
  accountId?: string
  name: string
  categoryName?: string
  subCategoryName?: string
  globalTagNames?: string
  groupTagNames?: string
  userTagNames?: string
  internationalFlg?: number
  hiddenSubscribersFlg?: number
  followersRange?: number
  followers: number
  followersUpdownRate: number
  engagements?: number
  engagementsPerPost?: number
  engagementRate?: number
  likes?: number
  likesPerPost?: number
  comments?: number
  commentsPerPost?: number
  shares?: number
  sharesPerPost?: number
  views?: number
  viewsPerPost?: number
  dislikes?: number
  dislikesPerPost?: number
  durations?: number
  durationsPerPost?: number
  retweets?: number
  retweetsPerPost?: number
  impressions?: number
  impressionsPerPost?: number
  replies?: number
  repliesPerPost?: number
  quotes?: number
  quotesPerPost?: number
  posts: number
  accounts?: number
}

/**
 * 分析相関サービス
 * @export
 * @class AnalyticsCorrelationService
 */
export class AnalyticsCorrelationService {
  public allAccounts: IAccountData[]
  public filteredAccounts: IAccountData[]
  public filteredAccountsWithoutFilterTag: IAccountData[]
  public accountTags: any
  // データ投稿あたりフラグ除外
  public dataPerPostExclude: string[]
  constructor() {
    this.allAccounts = []
    this.filteredAccounts = []
    this.dataPerPostExclude = ['posts', 'engagementRate', 'followers', 'followersUpdownRate']
  }

  /**
   * アカウントのフィルター
   * @param {{
   *     subCategoryName: string サブカテゴリー名
   *     accountTagFilter: string アカウントタグ
   *     followerRangeFilter: string[] フォロワー範囲
   *     accountGlobalFilter: number アカウントグローバルフラグ
   *   }} filter フィルター
   */
  public filterAccount(filter: {
    subCategoryName: string
    accountTagFilter: string[]
    followerRangeFilter: string[]
    accountGlobalFilter: number
  }) {
    if (!this.allAccounts.length) {
      return
    }
    // タグをフィルターしないアカウント一覧
    this.filteredAccountsWithoutFilterTag = []

    this.filteredAccounts = this.allAccounts.filter((account) => {
      // フォロワー数の範囲でフィルタリング
      if (filter.followerRangeFilter !== null) {
        const followerMatch = filter.followerRangeFilter.some((elem) => {
          if (account.hiddenSubscribersFlg === 1) {
            return elem === '非公開'
          }
          return account.followersRange === parseInt(elem.split('-')[0], 10)
        })

        if (followerMatch !== true) {
          return false
        }
      }

      // サブカテゴリーでフィルタリング
      if (filter.subCategoryName && filter.subCategoryName !== account.subCategoryName) {
        return false
      }

      // グローバルアカウントでフィルタリング
      if (filter.accountGlobalFilter && filter.accountGlobalFilter !== account.internationalFlg) {
        return false
      }

      this.filteredAccountsWithoutFilterTag.push(account)

      // アカウントタグでフィルタリング
      if (filter.accountTagFilter && filter.accountTagFilter.length > 0) {
        let accountTagNames = []
        if (account.globalTagNames !== null) {
          accountTagNames = accountTagNames.concat(account.globalTagNames.split(','))
        }

        if (account.groupTagNames !== null) {
          accountTagNames = accountTagNames.concat(account.groupTagNames.split(','))
        }

        if (account.userTagNames !== null) {
          accountTagNames = accountTagNames.concat(account.userTagNames.split(','))
        }
        const isMatched = this.matchTagName(accountTagNames, filter.accountTagFilter)
        if (!isMatched) {
          return false
        }
      }

      return true
    })
  }

  /**
   * アカウント一覧をセット
   * @param {string} snsType SNSタイプ
   * @param {*} accounts アカウントデータ
   * @returns {IAccountData[]} アカウント一覧
   */
  public setAccounts(snsType: string, accounts: any): IAccountData[] {
    if (!accounts || !accounts.length) {
      return []
    }
    const accountsData = []
    accounts.forEach((account) => {
      // フォロワー増減率
      const followersStart = account.followersStart
      let followersUpdownRate = null
      if (followersStart !== null && followersStart > 0) {
        followersUpdownRate = Number(((account.followersEnd / followersStart - 1) * 100).toFixed(2))
      }

      // 反応数
      const engagements = account.engagements
      // 全投稿のフォロワー数
      const followerPostTotal = account.followerPostTotal
      // 反応率
      let engagementRate = 0
      if (engagements > 0 && followerPostTotal > 0) {
        engagementRate = Number(((engagements / followerPostTotal) * 100).toFixed(2))
      }

      // アカウントのデータをセット
      const accountObject: IAccountData = {
        name: account.screenName,
        accountId: account.accountId,
        internationalFlg: 0,
        categoryName: account.brandCategoryName,
        subCategoryName: account.brandSubCategoryName,
        followers: account.followers,
        followersUpdownRate: followersUpdownRate,
        followersRange: account.followersRange,
        posts: account.posts ? account.posts : 0,
        globalTagNames: account.globalTagNames,
        groupTagNames: account.groupTagNames,
        userTagNames: account.userTagNames,
      }

      switch (snsType) {
        case 'facebook':
          accountObject.internationalFlg = account.internationalFlg
          accountObject.likes = account.likes ? account.likes : 0
          accountObject.comments = account.comments ? account.comments : 0
          accountObject.shares = account.shares ? account.shares : 0
          accountObject.engagements = account.engagements ? account.engagements : 0
          accountObject.engagementRate = engagementRate ? engagementRate : 0
          accountObject.likesPerPost = 0
          accountObject.commentsPerPost = 0
          accountObject.sharesPerPost = 0
          accountObject.engagementsPerPost = 0
          if (accountObject.posts > 0) {
            accountObject.likesPerPost = Number(
              (accountObject.likes / accountObject.posts).toFixed(2)
            )
            accountObject.commentsPerPost = Number(
              (accountObject.comments / accountObject.posts).toFixed(2)
            )
            accountObject.sharesPerPost = Number(
              (accountObject.shares / accountObject.posts).toFixed(2)
            )
            accountObject.engagementsPerPost = Number(
              (accountObject.engagements / accountObject.posts).toFixed(2)
            )
          }
          break

        case 'twitter':
          accountObject.engagementRate = engagementRate ? engagementRate : 0
          accountObject.engagements = account.engagements ? account.engagements : 0
          accountObject.impressions = account.impressions ? account.impressions : 0
          accountObject.quotes = account.quotes ? account.quotes : 0
          accountObject.replies = account.replies ? account.replies : 0
          accountObject.retweets = account.retweets ? account.retweets : 0
          accountObject.likes = account.likes ? account.likes : 0
          accountObject.retweetsPerPost = 0
          accountObject.likesPerPost = 0
          accountObject.impressionsPerPost = 0
          accountObject.quotesPerPost = 0
          accountObject.repliesPerPost = 0
          if (accountObject.posts > 0) {
            accountObject.retweetsPerPost = Number(
              (accountObject.retweets / accountObject.posts).toFixed(2)
            )
            accountObject.likesPerPost = Number(
              (accountObject.likes / accountObject.posts).toFixed(2)
            )
            accountObject.engagementsPerPost = Number(
              (accountObject.engagements / accountObject.posts).toFixed(2)
            )
            accountObject.impressionsPerPost = Number(
              (accountObject.impressions / accountObject.posts).toFixed(2)
            )
            accountObject.quotesPerPost = Number(
              (accountObject.quotes / accountObject.posts).toFixed(2)
            )
            accountObject.repliesPerPost = Number(
              (accountObject.replies / accountObject.posts).toFixed(2)
            )
          }
          break

        case 'instagram':
          accountObject.likes = account.likes ? account.likes : 0
          accountObject.comments = account.comments ? account.comments : 0
          accountObject.engagementRate = engagementRate ? engagementRate : 0
          accountObject.engagements = account.engagements ? account.engagements : 0
          accountObject.likesPerPost = 0
          accountObject.commentsPerPost = 0
          accountObject.engagementsPerPost = 0
          if (accountObject.posts > 0) {
            accountObject.likesPerPost = Number(
              (accountObject.likes / accountObject.posts).toFixed(2)
            )
            accountObject.commentsPerPost = Number(
              (accountObject.comments / accountObject.posts).toFixed(2)
            )
            accountObject.engagementsPerPost = Number(
              (accountObject.engagements / accountObject.posts).toFixed(2)
            )
          }
          break

        case 'youtube':
          accountObject.views = account.views ? account.views : 0
          accountObject.likes = account.likes ? account.likes : 0
          accountObject.dislikes = account.dislikes ? account.dislikes : 0
          accountObject.comments = account.comments ? account.comments : 0
          accountObject.durations = account.durations ? account.durations : 0
          accountObject.hiddenSubscribersFlg = account.hiddenSubscribersFlg
          accountObject.viewsPerPost = 0
          accountObject.likesPerPost = 0
          accountObject.dislikesPerPost = 0
          accountObject.commentsPerPost = 0
          accountObject.durationsPerPost = 0
          if (accountObject.posts > 0) {
            accountObject.viewsPerPost = Number(
              (accountObject.views / accountObject.posts).toFixed(2)
            )
            accountObject.likesPerPost = Number(
              (accountObject.likes / accountObject.posts).toFixed(2)
            )
            accountObject.dislikesPerPost = Number(
              (accountObject.dislikes / accountObject.posts).toFixed(2)
            )
            accountObject.commentsPerPost = Number(
              (accountObject.comments / accountObject.posts).toFixed(2)
            )
            accountObject.durationsPerPost = Number(
              (accountObject.durations / accountObject.posts).toFixed(2)
            )
          }
          break

        case 'line':
          accountObject.likes = account.likes ? account.likes : 0
          accountObject.comments = account.comments ? account.comments : 0
          accountObject.shares = account.shares ? account.shares : 0
          accountObject.engagements = account.engagements ? account.engagements : 0
          accountObject.engagementRate = engagementRate ? engagementRate : 0
          accountObject.likesPerPost = 0
          accountObject.commentsPerPost = 0
          accountObject.sharesPerPost = 0
          accountObject.engagementsPerPost = 0
          if (accountObject.posts > 0) {
            accountObject.likesPerPost = Number(
              (accountObject.likes / accountObject.posts).toFixed(2)
            )
            accountObject.commentsPerPost = Number(
              (accountObject.comments / accountObject.posts).toFixed(2)
            )
            accountObject.sharesPerPost = Number(
              (accountObject.shares / accountObject.posts).toFixed(2)
            )
            accountObject.engagementsPerPost = Number(
              (accountObject.engagements / accountObject.posts).toFixed(2)
            )
          }
          break
      }

      // アカウント一覧にアカウントのデータを追加
      accountsData.push(accountObject)
    })

    this.allAccounts = accountsData
  }

  /**
   * ブランドカテゴリデータをセット
   * @private
   * @param {IAccountData[]} accounts アカウント一覧
   * @return {IAccountData[]} アカウント一覧
   */
  public setBrandCategoryData(accounts: IAccountData[]): IAccountData[] {
    const objectDatas = []
    const brandCategoryKey = {}
    // データを格納
    accounts.forEach((account) => {
      if (!brandCategoryKey[account.categoryName]) {
        brandCategoryKey[account.categoryName] = {
          followers: 0,
          followersUpdownRate: 0,
          engagements: 0,
          engagementsPerPost: 0,
          engagementRate: 0,
          likes: 0,
          likesPerPost: 0,
          comments: 0,
          commentsPerPost: 0,
          shares: 0,
          sharesPerPost: 0,
          views: 0,
          viewsPerPost: 0,
          dislikes: 0,
          dislikesPerPost: 0,
          durations: 0,
          durationsPerPost: 0,
          retweets: 0,
          retweetsPerPost: 0,
          replies: 0,
          repliesPerPost: 0,
          quotes: 0,
          quotesPerPost: 0,
          impressions: 0,
          impressionsPerPost: 0,
          posts: 0,
          accounts: 0,
        }
      }
      brandCategoryKey[account.categoryName].followers += account.followers || 0
      brandCategoryKey[account.categoryName].followersUpdownRate += account.followersUpdownRate || 0
      brandCategoryKey[account.categoryName].engagements += account.engagements || 0
      brandCategoryKey[account.categoryName].engagementsPerPost += account.engagementsPerPost || 0
      brandCategoryKey[account.categoryName].engagementRate += account.engagementRate || 0
      brandCategoryKey[account.categoryName].likes += account.likes || 0
      brandCategoryKey[account.categoryName].likesPerPost += account.likesPerPost || 0
      brandCategoryKey[account.categoryName].comments += account.comments || 0
      brandCategoryKey[account.categoryName].commentsPerPost += account.commentsPerPost || 0
      brandCategoryKey[account.categoryName].shares += account.shares || 0
      brandCategoryKey[account.categoryName].sharesPerPost += account.sharesPerPost || 0
      brandCategoryKey[account.categoryName].views += account.views || 0
      brandCategoryKey[account.categoryName].viewsPerPost += account.viewsPerPost || 0
      brandCategoryKey[account.categoryName].dislikes += account.dislikes || 0
      brandCategoryKey[account.categoryName].dislikesPerPost += account.dislikesPerPost || 0
      brandCategoryKey[account.categoryName].durations += account.durations || 0
      brandCategoryKey[account.categoryName].durationsPerPost += account.durationsPerPost || 0
      brandCategoryKey[account.categoryName].retweets += account.retweets || 0
      brandCategoryKey[account.categoryName].retweetsPerPost += account.retweetsPerPost || 0
      brandCategoryKey[account.categoryName].replies += account.replies || 0
      brandCategoryKey[account.categoryName].repliesPerPost += account.repliesPerPost || 0
      brandCategoryKey[account.categoryName].quotes += account.quotes || 0
      brandCategoryKey[account.categoryName].quotesPerPost += account.quotesPerPost || 0
      brandCategoryKey[account.categoryName].impressions += account.impressions || 0
      brandCategoryKey[account.categoryName].impressionsPerPost += account.impressionsPerPost || 0
      brandCategoryKey[account.categoryName].posts += account.posts || 0
      brandCategoryKey[account.categoryName].accounts += 1
    })

    // 対象データに格納
    for (const categoryName in brandCategoryKey) {
      objectDatas.push({
        name: categoryName,
        followers: brandCategoryKey[categoryName].followers,
        followersUpdownRate: brandCategoryKey[categoryName].followersUpdownRate,
        engagements: brandCategoryKey[categoryName].engagements,
        engagementsPerPost: brandCategoryKey[categoryName].engagementsPerPost,
        engagementRate: brandCategoryKey[categoryName].engagementRate,
        likes: brandCategoryKey[categoryName].likes,
        likesPerPost: brandCategoryKey[categoryName].likesPerPost,
        comments: brandCategoryKey[categoryName].comments,
        commentsPerPost: brandCategoryKey[categoryName].commentsPerPost,
        shares: brandCategoryKey[categoryName].shares,
        sharesPerPost: brandCategoryKey[categoryName].sharesPerPost,
        views: brandCategoryKey[categoryName].views,
        viewsPerPost: brandCategoryKey[categoryName].viewsPerPost,
        dislikes: brandCategoryKey[categoryName].dislikes,
        dislikesPerPost: brandCategoryKey[categoryName].dislikesPerPost,
        durations: brandCategoryKey[categoryName].durations,
        durationsPerPost: brandCategoryKey[categoryName].durationsPerPost,
        retweets: brandCategoryKey[categoryName].retweets,
        retweetsPerPost: brandCategoryKey[categoryName].retweetsPerPost,
        replies: brandCategoryKey[categoryName].replies,
        repliesPerPost: brandCategoryKey[categoryName].repliesPerPost,
        quotes: brandCategoryKey[categoryName].quotes,
        quotesPerPost: brandCategoryKey[categoryName].quotesPerPost,
        impressions: brandCategoryKey[categoryName].impressions,
        impressionsPerPost: brandCategoryKey[categoryName].impressionsPerPost,
        posts: brandCategoryKey[categoryName].posts,
        accounts: brandCategoryKey[categoryName].accounts,
      })
    }

    return objectDatas
  }

  /**
   * ブランドサブカテゴリデータをセット
   * @param {IAccountData[]} accounts アカウント一覧
   * @private
   * @return {IAccountData[]} アカウント一覧
   */
  public setBrandSubCategoryData(accounts: IAccountData[]): IAccountData[] {
    const objectDatas = []
    const brandSubCategoryKey = {}
    // データを格納
    accounts.forEach((account) => {
      if (!brandSubCategoryKey[account.subCategoryName]) {
        brandSubCategoryKey[account.subCategoryName] = {
          followers: 0,
          followersUpdownRate: 0,
          engagements: 0,
          engagementsPerPost: 0,
          engagementRate: 0,
          likes: 0,
          likesPerPost: 0,
          comments: 0,
          commentsPerPost: 0,
          shares: 0,
          sharesPerPost: 0,
          views: 0,
          viewsPerPost: 0,
          dislikes: 0,
          dislikesPerPost: 0,
          durations: 0,
          durationsPerPost: 0,
          retweets: 0,
          retweetsPerPost: 0,
          replies: 0,
          repliesPerPost: 0,
          quotes: 0,
          quotesPerPost: 0,
          impressions: 0,
          impressionsPerPost: 0,
          posts: 0,
          accounts: 0,
        }
      }
      brandSubCategoryKey[account.subCategoryName].followers += account.followers || 0
      brandSubCategoryKey[account.subCategoryName].followersUpdownRate +=
        account.followersUpdownRate || 0
      brandSubCategoryKey[account.subCategoryName].engagements += account.engagements || 0
      brandSubCategoryKey[account.subCategoryName].engagementsPerPost +=
        account.engagementsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].engagementRate += account.engagementRate || 0
      brandSubCategoryKey[account.subCategoryName].likes += account.likes || 0
      brandSubCategoryKey[account.subCategoryName].likesPerPost += account.likesPerPost || 0
      brandSubCategoryKey[account.subCategoryName].comments += account.comments || 0
      brandSubCategoryKey[account.subCategoryName].commentsPerPost += account.commentsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].shares += account.shares || 0
      brandSubCategoryKey[account.subCategoryName].sharesPerPost += account.sharesPerPost || 0
      brandSubCategoryKey[account.subCategoryName].views += account.views || 0
      brandSubCategoryKey[account.subCategoryName].viewsPerPost += account.viewsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].dislikes += account.dislikes || 0
      brandSubCategoryKey[account.subCategoryName].dislikesPerPost += account.dislikesPerPost || 0
      brandSubCategoryKey[account.subCategoryName].durations += account.durations || 0
      brandSubCategoryKey[account.subCategoryName].durationsPerPost += account.durationsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].retweets += account.retweets || 0
      brandSubCategoryKey[account.subCategoryName].retweetsPerPost += account.retweetsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].replies += account.replies || 0
      brandSubCategoryKey[account.subCategoryName].repliesPerPost += account.repliesPerPost || 0
      brandSubCategoryKey[account.subCategoryName].quotes += account.quotes || 0
      brandSubCategoryKey[account.subCategoryName].quotesPerPost += account.quotesPerPost || 0
      brandSubCategoryKey[account.subCategoryName].impressions += account.impressions || 0
      brandSubCategoryKey[account.subCategoryName].impressionsPerPost +=
        account.impressionsPerPost || 0
      brandSubCategoryKey[account.subCategoryName].posts += account.posts || 0
      brandSubCategoryKey[account.subCategoryName].accounts += 1
    })

    // 対象データに格納
    for (const subCategoryName in brandSubCategoryKey) {
      objectDatas.push({
        name: subCategoryName,
        followers: brandSubCategoryKey[subCategoryName].followers,
        followersUpdownRate: brandSubCategoryKey[subCategoryName].followersUpdownRate,
        engagements: brandSubCategoryKey[subCategoryName].engagements,
        engagementsPerPost: brandSubCategoryKey[subCategoryName].engagementsPerPost,
        engagementRate: brandSubCategoryKey[subCategoryName].engagementRate,
        likes: brandSubCategoryKey[subCategoryName].likes,
        likesPerPost: brandSubCategoryKey[subCategoryName].likesPerPost,
        comments: brandSubCategoryKey[subCategoryName].comments,
        commentsPerPost: brandSubCategoryKey[subCategoryName].commentsPerPost,
        shares: brandSubCategoryKey[subCategoryName].shares,
        sharesPerPost: brandSubCategoryKey[subCategoryName].sharesPerPost,
        views: brandSubCategoryKey[subCategoryName].views,
        viewsPerPost: brandSubCategoryKey[subCategoryName].viewsPerPost,
        dislikes: brandSubCategoryKey[subCategoryName].dislikes,
        dislikesPerPost: brandSubCategoryKey[subCategoryName].dislikesPerPost,
        durations: brandSubCategoryKey[subCategoryName].durations,
        durationsPerPost: brandSubCategoryKey[subCategoryName].durationsPerPost,
        retweets: brandSubCategoryKey[subCategoryName].retweets,
        retweetsPerPost: brandSubCategoryKey[subCategoryName].retweetsPerPost,
        replies: brandSubCategoryKey[subCategoryName].replies,
        repliesPerPost: brandSubCategoryKey[subCategoryName].repliesPerPost,
        quotes: brandSubCategoryKey[subCategoryName].quotes,
        quotesPerPost: brandSubCategoryKey[subCategoryName].quotesPerPost,
        impressions: brandSubCategoryKey[subCategoryName].impressions,
        impressionsPerPost: brandSubCategoryKey[subCategoryName].impressionsPerPost,
        posts: brandSubCategoryKey[subCategoryName].posts,
        accounts: brandSubCategoryKey[subCategoryName].accounts,
      })
    }

    return objectDatas
  }

  /**
   * アカウント一覧データをソート
   * @param {IAccountData[]} account アカウント一覧
   * @param {string} sortBy キーソーと
   * @param {limit} limit リミット
   * @return {IAccountData[]} アカウント一覧
   */
  public sortAccountData(accounts: IAccountData[], sortBy: string, limit: number): IAccountData[] {
    // ソート
    accounts.sort((accountA, accountB) => {
      const aSortData = accountA[sortBy]
      const bSortData = accountB[sortBy]
      if (aSortData === null) {
        return 1
      }
      if (bSortData === null) {
        return -1
      }
      return aSortData > bSortData ? -1 : 1
    })
    if (!limit || limit <= 0) {
      return accounts
    }
    return accounts.slice(0, limit)
  }

  /**
   * アカウントタグ一覧を取得
   * @return {{value: string, count: number}[]} アカウントタグ一覧
   */
  public getTagList(): { value: string; count: number }[] {
    if (!this.filteredAccountsWithoutFilterTag || !this.filteredAccountsWithoutFilterTag.length) {
      return []
    }
    const accountTagKey = {}
    // アカウントタグを格納
    this.filteredAccountsWithoutFilterTag.forEach((account) => {
      // グローバルタグ
      if (account.globalTagNames) {
        const globalTags = account.globalTagNames.split(',')
        globalTags.forEach((tag) => {
          if (accountTagKey[tag] === undefined) {
            accountTagKey[tag] = 0
          }
          accountTagKey[tag] = accountTagKey[tag] + 1
        })
      }
      // グループタグ
      if (account.groupTagNames) {
        const groupTags = account.groupTagNames.split(',')
        groupTags.forEach((tag) => {
          if (accountTagKey[tag] === undefined) {
            accountTagKey[tag] = 0
          }
          accountTagKey[tag] = accountTagKey[tag] + 1
        })
      }
      // ユーザータグ
      if (account.userTagNames) {
        const userTags = account.userTagNames.split(',')
        userTags.forEach((tag) => {
          if (accountTagKey[tag] === undefined) {
            accountTagKey[tag] = 0
          }
          accountTagKey[tag] = accountTagKey[tag] + 1
        })
      }
    })

    const accountTag = []
    for (const tag in accountTagKey) {
      accountTag.push({
        value: tag,
        count: accountTagKey[tag],
      })
    }
    accountTag.sort((tagA, tagB) => {
      return tagA.count > tagB.count ? -1 : 1
    })
    return accountTag
  }

  /**
   * matchTagName タグをマッチ
   * @param  {string[]} accountTagList 対象アカウントのタグリスト
   * @param  {string[]} filterTagList 選択したタグリスト
   * @return {boolean} マッチ結果
   */
  private matchTagName(accountTagList: string[], filterTagList: string[]) {
    const notMatchFlag = filterTagList.some((filterTag) => {
      return accountTagList.indexOf(filterTag) === -1
    })

    return !notMatchFlag
  }
}

app.service('AnalyticsCorrelationService', AnalyticsCorrelationService)
